import React, { useState, useEffect } from 'react';
import { database } from '../../firebase/config';
import { ref, get, update } from 'firebase/database';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // TDS Settings
  const [tdsSettings, setTdsSettings] = useState({
    enabled: false,
    percentage: 0
  });
  const [showTdsForm, setShowTdsForm] = useState(false);
  const [tdsPercentage, setTdsPercentage] = useState('');

  // Admin Charges Settings
  const [adminCharges, setAdminCharges] = useState({
    enabled: false,
    percentage: 0
  });
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [adminChargesPercentage, setAdminChargesPercentage] = useState('');

  // GST Settings
  const [gstSettings, setGstSettings] = useState({
    enabled: false,
    percentage: 5
  });
  const [showGstForm, setShowGstForm] = useState(false);
  const [gstPercentage, setGstPercentage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);

      // Fetch TDS settings
      const tdsRef = ref(database, 'HTAMS/settings/tds');
      const tdsSnapshot = await get(tdsRef);
      if (tdsSnapshot.exists()) {
        const tdsData = tdsSnapshot.val();
        setTdsSettings(tdsData);
      }

      // Fetch Admin Charges settings
      const adminRef = ref(database, 'HTAMS/settings/adminCharges');
      const adminSnapshot = await get(adminRef);
      if (adminSnapshot.exists()) {
        const adminData = adminSnapshot.val();
        setAdminCharges(adminData);
      }

      // Fetch GST settings
      const gstRef = ref(database, 'HTAMS/settings/gst');
      const gstSnapshot = await get(gstRef);
      if (gstSnapshot.exists()) {
        const gstData = gstSnapshot.val();
        setGstSettings(gstData);
      }

    } catch (error) {
      console.error('Error fetching settings:', error);
      alert('Error loading settings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openTdsForm = () => {
    setTdsPercentage(tdsSettings.percentage || '');
    setShowTdsForm(true);
  };

  const openAdminForm = () => {
    setAdminChargesPercentage(adminCharges.percentage || '');
    setShowAdminForm(true);
  };

  const openGstForm = () => {
    setGstPercentage(gstSettings.percentage || '');
    setShowGstForm(true);
  };

  const saveTdsSettings = async () => {
    const percentage = parseFloat(tdsPercentage);

    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      alert('Please enter a valid TDS percentage between 0 and 100');
      return;
    }

    try {
      setIsProcessing(true);
      const tdsData = {
        enabled: percentage > 0,
        percentage: percentage,
        updatedAt: Date.now(),
        updatedBy: 'admin'
      };

      await update(ref(database, 'HTAMS/settings'), { tds: tdsData });
      setTdsSettings(tdsData);
      setShowTdsForm(false);
      alert(`TDS settings updated successfully! ${percentage > 0 ? `Enabled at ${percentage}%` : 'Disabled'}`);
    } catch (error) {
      console.error('Error saving TDS settings:', error);
      alert('Error saving TDS settings: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const saveAdminChargesSettings = async () => {
    const percentage = parseFloat(adminChargesPercentage);

    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      alert('Please enter a valid Admin Charges percentage between 0 and 100');
      return;
    }

    try {
      setIsProcessing(true);
      const adminData = {
        enabled: percentage > 0,
        percentage: percentage,
        updatedAt: Date.now(),
        updatedBy: 'admin'
      };

      await update(ref(database, 'HTAMS/settings'), { adminCharges: adminData });
      setAdminCharges(adminData);
      setShowAdminForm(false);
      alert(`Admin Charges settings updated successfully! ${percentage > 0 ? `Enabled at ${percentage}%` : 'Disabled'}`);
    } catch (error) {
      console.error('Error saving Admin Charges settings:', error);
      alert('Error saving Admin Charges settings: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const saveGstSettings = async () => {
    const percentage = parseFloat(gstPercentage);

    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      alert('Please enter a valid GST percentage between 0 and 100');
      return;
    }

    try {
      setIsProcessing(true);
      const gstData = {
        enabled: percentage > 0,
        percentage: percentage,
        updatedAt: Date.now(),
        updatedBy: 'admin'
      };

      await update(ref(database, 'HTAMS/settings'), { gst: gstData });
      setGstSettings(gstData);
      setShowGstForm(false);
      alert(`GST settings updated successfully! ${percentage > 0 ? `Enabled at ${percentage}%` : 'Disabled'}`);
    } catch (error) {
      console.error('Error saving GST settings:', error);
      alert('Error saving GST settings: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Settings</h1>
          <p style={styles.subtitle}>Manage TDS, Admin Charges, and GST Settings</p>
        </div>

        <div style={styles.cardsGrid}>
          {/* TDS Settings Card */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>TDS Settings</h3>
              <button
                onClick={openTdsForm}
                style={styles.updateButton}
              >
                Update
              </button>
            </div>

            <div style={{
              ...styles.statusBox,
              background: tdsSettings.enabled ? '#d1fae5' : '#fee2e2',
              border: `1px solid ${tdsSettings.enabled ? '#10b981' : '#ef4444'}`
            }}>
              <p style={styles.statusText}>
                <strong>Status:</strong> {tdsSettings.enabled ? 'Enabled' : 'Disabled'}
              </p>
              <p style={styles.statusText}>
                <strong>Percentage:</strong> {tdsSettings.percentage}%
              </p>
              {tdsSettings.updatedAt && (
                <p style={styles.statusText}>
                  <small>Last updated: {new Date(tdsSettings.updatedAt).toLocaleString()}</small>
                </p>
              )}
            </div>

            {showTdsForm && (
              <div style={styles.formContainer}>
                <div style={styles.infoBox}>
                  <p style={styles.infoText}>
                    <strong>Note:</strong> TDS will be automatically deducted from all approved withdrawal requests. Enter 0 to disable.
                  </p>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>TDS Percentage (%) *</label>
                  <input
                    type="number"
                    value={tdsPercentage}
                    onChange={(e) => setTdsPercentage(e.target.value)}
                    placeholder="Enter TDS percentage (0-100)"
                    min="0"
                    max="100"
                    step="0.01"
                    style={styles.input}
                  />
                  <small style={styles.smallText}>Enter 0 to disable TDS, or a value between 0.01 and 100.</small>
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    onClick={saveTdsSettings}
                    disabled={isProcessing || tdsPercentage === ''}
                    style={{
                      ...styles.button,
                      ...styles.buttonSave,
                      ...(isProcessing || tdsPercentage === '' ? styles.buttonDisabled : {})
                    }}
                  >
                    {isProcessing ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setShowTdsForm(false)}
                    disabled={isProcessing}
                    style={{ ...styles.button, ...styles.buttonCancel }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Admin Charges Settings Card */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Admin Charges Settings</h3>
              <button
                onClick={openAdminForm}
                style={styles.updateButton}
              >
                Update
              </button>
            </div>

            <div style={{
              ...styles.statusBox,
              background: adminCharges.enabled ? '#d1fae5' : '#fee2e2',
              border: `1px solid ${adminCharges.enabled ? '#10b981' : '#ef4444'}`
            }}>
              <p style={styles.statusText}>
                <strong>Status:</strong> {adminCharges.enabled ? 'Enabled' : 'Disabled'}
              </p>
              <p style={styles.statusText}>
                <strong>Percentage:</strong> {adminCharges.percentage}%
              </p>
              {adminCharges.updatedAt && (
                <p style={styles.statusText}>
                  <small>Last updated: {new Date(adminCharges.updatedAt).toLocaleString()}</small>
                </p>
              )}
            </div>

            {showAdminForm && (
              <div style={styles.formContainer}>
                <div style={styles.infoBox}>
                  <p style={styles.infoText}>
                    <strong>Note:</strong> Admin charges will be automatically deducted from withdrawal requests. Enter 0 to disable.
                  </p>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Admin Charges Percentage (%) *</label>
                  <input
                    type="number"
                    value={adminChargesPercentage}
                    onChange={(e) => setAdminChargesPercentage(e.target.value)}
                    placeholder="Enter Admin Charges percentage (0-100)"
                    min="0"
                    max="100"
                    step="0.01"
                    style={styles.input}
                  />
                  <small style={styles.smallText}>Enter 0 to disable Admin Charges, or a value between 0.01 and 100.</small>
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    onClick={saveAdminChargesSettings}
                    disabled={isProcessing || adminChargesPercentage === ''}
                    style={{
                      ...styles.button,
                      ...styles.buttonSave,
                      ...(isProcessing || adminChargesPercentage === '' ? styles.buttonDisabled : {})
                    }}
                  >
                    {isProcessing ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setShowAdminForm(false)}
                    disabled={isProcessing}
                    style={{ ...styles.button, ...styles.buttonCancel }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* GST Settings Card */}
          {/* <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>GST Settings</h3>
              <button
                onClick={openGstForm}
                style={styles.updateButton}
              >
                Update
              </button>
            </div>
            
            <div style={{
              ...styles.statusBox,
              background: gstSettings.enabled ? '#d1fae5' : '#fee2e2',
              border: `1px solid ${gstSettings.enabled ? '#10b981' : '#ef4444'}`
            }}>
              <p style={styles.statusText}>
                <strong>Status:</strong> {gstSettings.enabled ? 'Enabled' : 'Disabled'}
              </p>
              <p style={styles.statusText}>
                <strong>Percentage:</strong> {gstSettings.percentage}%
              </p>
              {gstSettings.updatedAt && (
                <p style={styles.statusText}>
                  <small>Last updated: {new Date(gstSettings.updatedAt).toLocaleString()}</small>
                </p>
              )}
            </div>

            {showGstForm && (
              <div style={styles.formContainer}>
                <div style={styles.infoBox}>
                  <p style={styles.infoText}>
                    <strong>Note:</strong> GST will be automatically calculated and included in all invoices based on the configured percentage.
                  </p>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>GST Percentage (%) *</label>
                  <input
                    type="number"
                    value={gstPercentage}
                    onChange={(e) => setGstPercentage(e.target.value)}
                    placeholder="Enter GST percentage (0-100)"
                    min="0"
                    max="100"
                    step="0.01"
                    style={styles.input}
                  />
                  <small style={styles.smallText}>Enter 0 to disable GST, or a value between 0.01 and 100.</small>
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    onClick={saveGstSettings}
                    disabled={isProcessing || gstPercentage === ''}
                    style={{
                      ...styles.button,
                      ...styles.buttonSave,
                      ...(isProcessing || gstPercentage === '' ? styles.buttonDisabled : {})
                    }}
                  >
                    {isProcessing ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setShowGstForm(false)}
                    disabled={isProcessing}
                    style={{...styles.button, ...styles.buttonCancel}}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div> */}
        </div>
      </div>
    </>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '100%',
    margin: '0 auto',
    backgroundColor: 'var(--mobile-bg-primary)',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--mobile-text-primary)',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: 'var(--mobile-text-secondary)',
    fontSize: '14px',
    margin: '0',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0',
  },
  updateButton: {
    padding: '8px 20px',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
  },
  statusBox: {
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '15px',
  },
  statusText: {
    margin: '5px 0',
    fontSize: '14px',
    color: '#374151',
  },
  formContainer: {
    animation: 'slideDown 0.3s ease',
    borderTop: '2px solid #e5e7eb',
    paddingTop: '20px',
    marginTop: '15px',
  },
  infoBox: {
    background: '#fef3c7',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '15px',
    border: '1px solid #fbbf24',
  },
  infoText: {
    margin: '0',
    fontSize: '13px',
    color: '#92400e',
  },
  formGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  smallText: {
    display: 'block',
    color: '#6b7280',
    fontSize: '12px',
    marginTop: '5px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginTop: '15px',
  },
  button: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
  },
  buttonSave: {
    background: '#4caf50',
    color: 'white',
  },
  buttonCancel: {
    background: '#6b7280',
    color: 'white',
  },
  buttonDisabled: {
    background: '#9ca3af',
    cursor: 'not-allowed',
  },
};

export default Settings;
